import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { FileSignature, Loader2, Plus, Edit, Trash2, Search, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ChevronsUpDown } from 'lucide-react';

const API_BASE = 'https://api1.simplyworkcrm.com/api:JBYRWqH3';

export default function AdminContracting() {
  // Get user info from localStorage
  const userInfo = JSON.parse(localStorage.getItem('user_info') || '{}');
  
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAgencies, setSelectedAgencies] = useState([]);
  const [agencyOpen, setAgencyOpen] = useState(false);
  const [selectedUplines, setSelectedUplines] = useState([]);
  const [uplineOpen, setUplineOpen] = useState(false);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [statusOpen, setStatusOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [deletingAgent, setDeletingAgent] = useState(null);
  const [agencies, setAgencies] = useState([]);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    npn: '',
    agencyId: '',
    uplineNpn: '',
    status: 'active'
  });
  const [npnStatus, setNpnStatus] = useState({ verified: false, checking: false, error: '' });
  const [uplineStatus, setUplineStatus] = useState({ verified: false, checking: false, name: '', error: '' });
  const [isEditingNPN, setIsEditingNPN] = useState(false);
  const [originalNPN, setOriginalNPN] = useState('');
  
  // Bulk actions
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [bulkAction, setBulkAction] = useState('');
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [selectedBulkStatus, setSelectedBulkStatus] = useState('');
  
  // Agency data from API
  const [agencyList, setAgencyList] = useState([]);
  
  // Add Agency modal
  const [showAddAgencyModal, setShowAddAgencyModal] = useState(false);
  const [agencyFormData, setAgencyFormData] = useState({
    name: '',
    agentmanager_id: '',
    managingagency_id: ''
  });

  useEffect(() => {
    fetchAgents();
  }, []);

  const fetchAgents = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE}/portal/internal/contracting/allRecords`);
      const agentsData = Array.isArray(response.data) ? response.data : [];
      setAgents(agentsData);
    } catch (err) {
      console.error('Error fetching agents:', err);
      toast.error('Failed to load agent records');
    } finally {
      setLoading(false);
    }
  };

  const fetchAgencies = async () => {
    try {
      const response = await axios.get(`${API_BASE}/meta/contracting/agencies`);
      const agenciesData = Array.isArray(response.data) ? response.data : [];
      setAgencies(agenciesData);
      setAgencyList(agenciesData);
    } catch (err) {
      console.error('Error fetching agencies:', err);
      toast.error('Failed to load agencies');
    }
  };

  useEffect(() => {
    fetchAgencies();
  }, []);

  // Get unique agencies for filter
  const uniqueAgencies = useMemo(() => {
    const agencySet = new Set();
    agents.forEach(agent => {
      if (agent.agencyName) {
        agencySet.add(agent.agencyName);
      }
    });
    return Array.from(agencySet).sort();
  }, [agents]);

  // Get unique uplines for filter
  const uniqueUplines = useMemo(() => {
    const uplineSet = new Set();
    agents.forEach(agent => {
      if (agent.agentUpline_name) {
        uplineSet.add(agent.agentUpline_name);
      }
    });
    return Array.from(uplineSet).sort();
  }, [agents]);

  // Get unique statuses for filter
  const uniqueStatuses = useMemo(() => {
    const statusSet = new Set();
    agents.forEach(agent => {
      if (agent.status) {
        statusSet.add(agent.status);
      }
    });
    return Array.from(statusSet).sort();
  }, [agents]);

  // Filter agents
  const filteredAgents = useMemo(() => {
    let filtered = agents;

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(agent => 
        (agent.first_name && agent.first_name.toLowerCase().includes(query)) ||
        (agent.last_name && agent.last_name.toLowerCase().includes(query)) ||
        (agent.npn && agent.npn.toString().includes(query)) ||
        (agent.agencyName && agent.agencyName.toLowerCase().includes(query)) ||
        (agent.agentUpline_name && agent.agentUpline_name.toLowerCase().includes(query))
      );
    }

    // Agency filter
    if (selectedAgencies.length > 0) {
      filtered = filtered.filter(agent => 
        selectedAgencies.includes(agent.agencyName)
      );
    }

    // Upline filter
    if (selectedUplines.length > 0) {
      filtered = filtered.filter(agent => 
        selectedUplines.includes(agent.agentUpline_name)
      );
    }

    // Status filter
    if (selectedStatuses.length > 0) {
      filtered = filtered.filter(agent => 
        selectedStatuses.includes(agent.status)
      );
    }

    return filtered;
  }, [agents, searchQuery, selectedAgencies, selectedUplines, selectedStatuses]);

  // Summary statistics
  const summaryStats = useMemo(() => {
    const total = filteredAgents.length;
    const active = filteredAgents.filter(a => a.status === 'active').length;
    const disconnected = filteredAgents.filter(a => a.status === 'disconnected').length;
    return { total, active, disconnected };
  }, [filteredAgents]);

  // Agency summary with API data
  const agencySummary = useMemo(() => {
    // Count agents per agency from filtered agents
    const agentCounts = new Map();
    filteredAgents.forEach(agent => {
      const agencyName = agent.agencyName || 'Unknown';
      agentCounts.set(agencyName, (agentCounts.get(agencyName) || 0) + 1);
    });

    // Create summary with API agency data
    const summary = [];
    
    // Add agencies from API
    agencyList.forEach(agency => {
      summary.push({
        id: agency.id,
        name: agency.name,
        manager: agency.agencyManager_name || 'N/A',
        managingAgency: agency.managingAgency_name || 'N/A',
        count: agentCounts.get(agency.name) || 0,
        isKnown: true
      });
      // Remove from counts map
      agentCounts.delete(agency.name);
    });

    // Add remaining "Unknown" agencies (agencies in data but not in API)
    agentCounts.forEach((count, name) => {
      if (name !== 'Unknown' && count > 0) {
        summary.push({
          id: null,
          name: name,
          manager: 'Unknown',
          managingAgency: 'Unknown',
          count: count,
          isKnown: false
        });
      }
    });

    return summary.sort((a, b) => b.count - a.count);
  }, [filteredAgents, agencyList]);

  // Upline summary with agency info
  const uplineSummary = useMemo(() => {
    const uplineMap = new Map();
    filteredAgents.forEach(agent => {
      const uplineName = agent.agentUpline_name || 'Unknown';
      if (!uplineMap.has(uplineName)) {
        uplineMap.set(uplineName, { 
          name: uplineName, 
          agency: agent.agencyName || 'Unknown',
          count: 0 
        });
      }
      uplineMap.get(uplineName).count++;
    });
    return Array.from(uplineMap.values()).sort((a, b) => b.count - a.count);
  }, [filteredAgents]);

  // Pagination for main table
  const paginatedAgents = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredAgents.slice(startIndex, endIndex);
  }, [filteredAgents, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredAgents.length / itemsPerPage);

  // Managing Agency filter
  const [selectedManagingAgency, setSelectedManagingAgency] = useState('');

  // Get unique managing agencies
  const uniqueManagingAgencies = useMemo(() => {
    const managingSet = new Set();
    agencyList.forEach(agency => {
      if (agency.managingAgency_name) {
        managingSet.add(agency.managingAgency_name);
      }
    });
    return Array.from(managingSet).sort();
  }, [agencyList]);

  // Filter agency summary by managing agency
  const filteredAgencySummary = useMemo(() => {
    if (!selectedManagingAgency) return agencySummary;
    return agencySummary.filter(agency => {
      const agencyData = agencyList.find(a => a.name === agency.name);
      return agencyData?.managingAgency_name === selectedManagingAgency;
    });
  }, [agencySummary, selectedManagingAgency, agencyList]);

  // Pagination for agency summary
  const [agencyPage, setAgencyPage] = useState(1);
  const [agencyItemsPerPage] = useState(10);
  const paginatedAgencies = useMemo(() => {
    const startIndex = (agencyPage - 1) * agencyItemsPerPage;
    return filteredAgencySummary.slice(startIndex, startIndex + agencyItemsPerPage);
  }, [filteredAgencySummary, agencyPage, agencyItemsPerPage]);
  const agencyTotalPages = Math.ceil(filteredAgencySummary.length / agencyItemsPerPage);

  // Pagination for upline summary
  const [uplinePage, setUplinePage] = useState(1);
  const [uplineItemsPerPage] = useState(10);
  const paginatedUplines = useMemo(() => {
    const startIndex = (uplinePage - 1) * uplineItemsPerPage;
    return uplineSummary.slice(startIndex, startIndex + uplineItemsPerPage);
  }, [uplineSummary, uplinePage, uplineItemsPerPage]);
  const uplineTotalPages = Math.ceil(uplineSummary.length / uplineItemsPerPage);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
    setAgencyPage(1);
    setUplinePage(1);
  }, [searchQuery, selectedAgencies, selectedUplines, selectedStatuses, selectedManagingAgency]);

  // NPN Verification (must return "Not Found")
  const verifyNPN = async (npn) => {
    if (!npn || npn.length < 5) {
      setNpnStatus({ verified: false, checking: false, error: 'NPN must be at least 5 digits' });
      return;
    }

    setNpnStatus({ verified: false, checking: true, error: '' });
    
    try {
      const response = await axios.get(`${API_BASE}/auth/verifyNPN`, {
        params: { npn }
      });
      
      // Check if response indicates "Not Found"
      if (response.data?.message === 'Not Found' || response.data?.error === 'Not Found' || response.data?.status === 'not_found') {
        setNpnStatus({ verified: true, checking: false, error: '' });
        toast.success('NPN verified - available for registration');
      } else {
        setNpnStatus({ verified: false, checking: false, error: 'NPN already exists in system' });
        toast.error('NPN already exists');
      }
    } catch (err) {
      // If API returns 404 or error, that's what we want (Not Found)
      if (err.response?.status === 404 || err.response?.data?.message === 'Not Found') {
        setNpnStatus({ verified: true, checking: false, error: '' });
        toast.success('NPN verified - available for registration');
      } else {
        setNpnStatus({ verified: false, checking: false, error: 'Verification failed' });
        toast.error('Failed to verify NPN');
      }
    }
  };

  // Upline NPN Verification (must be active)
  const verifyUplineNPN = async (npn) => {
    if (!npn || npn.length < 5) {
      setUplineStatus({ verified: false, checking: false, name: '', error: 'NPN must be at least 5 digits' });
      return;
    }

    setUplineStatus({ verified: false, checking: true, name: '', error: '' });
    
    try {
      const response = await axios.get(`${API_BASE}/auth/verifyNPN`, {
        params: { npn }
      });
      
      // Check if agent exists and is active
      if (response.data?.first_name && response.data?.status === 'active') {
        const fullName = `${response.data.first_name} ${response.data.last_name || ''}`.trim();
        setUplineStatus({ 
          verified: true, 
          checking: false, 
          name: fullName, 
          error: '' 
        });
        toast.success(`Upline verified: ${fullName}`);
      } else if (response.data?.first_name && response.data?.status !== 'active') {
        setUplineStatus({ verified: false, checking: false, name: '', error: 'Agent is not active' });
        toast.error('Upline agent is not active');
      } else {
        setUplineStatus({ verified: false, checking: false, name: '', error: 'Agent not found' });
        toast.error('Upline agent not found');
      }
    } catch (err) {
      setUplineStatus({ verified: false, checking: false, name: '', error: 'Verification failed' });
      toast.error('Failed to verify upline NPN');
    }
  };

  const handleOpenAddModal = () => {
    fetchAgencies();
    setFormData({
      firstName: '',
      lastName: '',
      npn: '',
      agencyId: '',
      uplineNpn: '',
      status: 'active'
    });
    setNpnStatus({ verified: false, checking: false, error: '' });
    setUplineStatus({ verified: false, checking: false, name: '', error: '' });
    setShowAddModal(true);
  };

  const handleSubmitForm = () => {
    // Validation
    if (!formData.firstName || !formData.lastName) {
      toast.error('First name and last name are required');
      return;
    }
    if (!npnStatus.verified) {
      toast.error('Please verify NPN first');
      return;
    }
    if (!formData.agencyId) {
      toast.error('Please select an agency');
      return;
    }
    if (!formData.uplineNpn) {
      toast.error('Upline NPN is required');
      return;
    }
    if (!uplineStatus.verified) {
      toast.error('Please verify upline NPN');
      return;
    }
    if (!formData.status) {
      toast.error('Please select a status');
      return;
    }

    // Show confirmation modal
    setShowAddModal(false);
    setShowConfirmModal(true);
  };

  const handleConfirmAdd = async () => {
    try {
      const payload = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        npn: formData.npn,
        agencyId: formData.agencyId,
        uplineNpn: formData.uplineNpn,
        status: formData.status,
        submittedBy_id: userInfo.user_id
      };

      await axios.post('https://api1.simplyworkcrm.com/api:JBYRWqH3/portal/internal/contracting/add', payload);
      
      toast.success('Agent added successfully!');
      setShowConfirmModal(false);
      setShowAddModal(false);
      fetchAgents(); // Refresh list
    } catch (err) {
      console.error('Error adding agent:', err);
      toast.error('Failed to add agent');
    }
  };

  const handleOpenEditModal = (agent) => {
    fetchAgencies();
    setEditingAgent(agent);
    setOriginalNPN(agent.npn || '');
    setFormData({
      firstName: agent.first_name || '',
      lastName: agent.last_name || '',
      email: agent.email || '',
      npn: agent.npn || '',
      agencyId: agent.agencyId || '',
      uplineNpn: agent.agentUpline_npn || '',
      status: agent.status || 'active'
    });
    // For edit, NPN is initially verified (existing record)
    setNpnStatus({ verified: true, checking: false, error: '' });
    setIsEditingNPN(false);
    // Check if upline exists
    if (agent.agentUpline_name) {
      setUplineStatus({ 
        verified: true, 
        checking: false, 
        name: agent.agentUpline_name, 
        error: '' 
      });
    } else {
      setUplineStatus({ verified: false, checking: false, name: '', error: '' });
    }
    setShowEditModal(true);
  };

  const handleSubmitEdit = () => {
    // Validation
    if (!formData.firstName || !formData.lastName) {
      toast.error('First name and last name are required');
      return;
    }
    if (!npnStatus.verified) {
      toast.error('Please verify NPN first');
      return;
    }
    if (!formData.agencyId) {
      toast.error('Please select an agency');
      return;
    }
    if (!formData.uplineNpn) {
      toast.error('Upline NPN is required');
      return;
    }
    if (!uplineStatus.verified) {
      toast.error('Please verify upline NPN');
      return;
    }
    if (!formData.status) {
      toast.error('Please select a status');
      return;
    }

    // Show confirmation modal
    setShowEditModal(false);
    setShowConfirmModal(true);
  };

  const handleConfirmEdit = async () => {
    try {
      const payload = {
        agent_id: editingAgent.id,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        npn: formData.npn,
        agencyId: formData.agencyId,
        uplineNpn: formData.uplineNpn,
        status: formData.status,
        submittedBy_id: userInfo.user_id
      };

      await axios.post('https://api1.simplyworkcrm.com/api:JBYRWqH3/portal/internal/contracting/updateRecord', payload);
      
      toast.success('Agent updated successfully!');
      setShowConfirmModal(false);
      setShowEditModal(false);
      setEditingAgent(null);
      fetchAgents(); // Refresh list
    } catch (err) {
      console.error('Error updating agent:', err);
      toast.error('Failed to update agent');
    }
  };

  const handleOpenDeleteModal = (agent) => {
    setDeletingAgent(agent);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await axios.post('https://api1.simplyworkcrm.com/api:JBYRWqH3/portal/internal/contracting/delete', {
        agent_id: [deletingAgent.id]
      });
      
      toast.success('Agent deleted successfully!');
      setShowDeleteModal(false);
      setDeletingAgent(null);
      fetchAgents(); // Refresh list
    } catch (err) {
      console.error('Error deleting agent:', err);
      toast.error('Failed to delete agent');
    }
  };

  // Add Agency Handler
  const handleOpenAddAgencyModal = () => {
    setAgencyFormData({
      name: '',
      agentmanager_id: '',
      managingagency_id: ''
    });
    setShowAddAgencyModal(true);
  };

  const handleSubmitAddAgency = async () => {
    // Validation
    if (!agencyFormData.name) {
      toast.error('Agency name is required');
      return;
    }
    
    try {
      await axios.post('https://api1.simplyworkcrm.com/api:JBYRWqH3/portal/internal/contracting/agency/add', {
        name: agencyFormData.name,
        agentmanager_id: agencyFormData.agentmanager_id || null,
        managingagency_id: agencyFormData.managingagency_id || null
      });
      
      toast.success('Agency added successfully!');
      setShowAddAgencyModal(false);
      fetchAgencies(); // Refresh agency list
    } catch (err) {
      console.error('Error adding agency:', err);
      toast.error('Failed to add agency');
    }
  };

  // Bulk Actions Handlers
  const handleBulkDelete = async () => {
    if (selectedAgents.length === 0) return;
    
    try {
      await axios.post('https://api1.simplyworkcrm.com/api:JBYRWqH3/portal/internal/contracting/delete', {
        agent_id: selectedAgents
      });
      
      toast.success(`${selectedAgents.length} agent(s) deleted successfully!`);
      setSelectedAgents([]);
      setBulkAction('');
      setShowBulkModal(false);
      fetchAgents();
    } catch (err) {
      console.error('Error deleting agents:', err);
      toast.error('Failed to delete agents');
    }
  };

  const handleBulkChangeAgency = async (agencyId) => {
    if (selectedAgents.length === 0 || !agencyId) return;
    
    try {
      await axios.post('https://api1.simplyworkcrm.com/api:JBYRWqH3/portal/internal/contracting/updateAgency', {
        agent_id: selectedAgents,
        agency_id: agencyId
      });
      
      toast.success(`${selectedAgents.length} agent(s) updated successfully!`);
      setSelectedAgents([]);
      setBulkAction('');
      setShowBulkModal(false);
      fetchAgents();
    } catch (err) {
      console.error('Error updating agencies:', err);
      toast.error('Failed to update agencies');
    }
  };

  const handleBulkChangeUpline = async (uplineId) => {
    if (selectedAgents.length === 0 || !uplineId) return;
    
    try {
      await axios.post('https://api1.simplyworkcrm.com/api:JBYRWqH3/portal/internal/contracting/updateUpline', {
        agent_id: selectedAgents,
        upline_id: uplineId
      });
      
      toast.success(`${selectedAgents.length} agent(s) updated successfully!`);
      setSelectedAgents([]);
      setBulkAction('');
      setShowBulkModal(false);
      fetchAgents();
    } catch (err) {
      console.error('Error updating uplines:', err);
      toast.error('Failed to update uplines');
    }
  };

  const handleBulkUpdateStatus = async () => {
    if (selectedAgents.length === 0 || !selectedBulkStatus) {
      toast.error('Please select a status');
      return;
    }
    
    try {
      await axios.post('https://api1.simplyworkcrm.com/api:JBYRWqH3/portal/internal/contracting/updateStatus', {
        agent_id: selectedAgents,
        status: selectedBulkStatus
      });
      
      toast.success(`${selectedAgents.length} agent(s) status updated to ${selectedBulkStatus}!`);
      setSelectedAgents([]);
      setBulkAction('');
      setSelectedBulkStatus('');
      setShowBulkModal(false);
      fetchAgents();
    } catch (err) {
      console.error('Error updating status:', err);
      toast.error('Failed to update status');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#d4af37] mx-auto mb-4" />
          <p className="text-neutral-600">Loading contracting records...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-neutral-800">Contracting</h1>
        <p className="text-sm text-neutral-600 mt-1">
          Manage agent contracts and registrations
        </p>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="border-0 shadow-sm rounded-xl">
          <CardContent className="p-4">
            <div className="text-center">
              <p className="text-xs text-neutral-600 mb-1">Total Agents</p>
              <p className="text-2xl font-bold text-neutral-800">{summaryStats.total}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm rounded-xl">
          <CardContent className="p-4">
            <div className="text-center">
              <p className="text-xs text-neutral-600 mb-1">Active</p>
              <p className="text-2xl font-bold text-green-600">{summaryStats.active}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm rounded-xl">
          <CardContent className="p-4">
            <div className="text-center">
              <p className="text-xs text-neutral-600 mb-1">Disconnected</p>
              <p className="text-2xl font-bold text-red-600">{summaryStats.disconnected}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content: Table (65%) + Summaries (35%) */}
      <div className="flex gap-6">
        {/* Main Table - 65% */}
        <div style={{ width: '65%' }}>
          <Card className="border-0 shadow-sm rounded-xl">
            <CardContent className="p-6">
          {/* Search and Add Agent */}
          <div className="mb-4 flex gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <Input
                placeholder="Search by name, NPN, agency, or upline..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button 
              onClick={handleOpenAddModal}
              className="bg-[#d4af37] hover:bg-[#b8941e] text-white whitespace-nowrap"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add New Agent
            </Button>
          </div>

          {/* Filters Row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            {/* Agency Filter */}
            <div>
              <label className="text-xs text-neutral-600 mb-1 block">Filter by Agency</label>
              <Popover open={agencyOpen} onOpenChange={setAgencyOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={agencyOpen}
                    className="w-full justify-between"
                  >
                    {selectedAgencies.length === 0
                      ? "All Agencies"
                      : `${selectedAgencies.length} selected`}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0">
                  <div className="flex flex-col">
                    <div className="flex items-center border-b px-3">
                      <svg className="mr-2 h-4 w-4 shrink-0 opacity-50" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <input
                        className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-neutral-500"
                        placeholder="Search agencies..."
                      />
                    </div>
                    <div className="max-h-64 overflow-auto">
                      {uniqueAgencies.map((agency) => (
                        <div className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-2 text-sm outline-none hover:bg-neutral-100 active:bg-neutral-200"
                          key={agency}
                          onClick={() => {
                            setSelectedAgencies(prev =>
                              prev.includes(agency)
                                ? prev.filter(a => a !== agency)
                                : [...prev, agency]
                            );
                          }}
                        >
                          <Checkbox
                            checked={selectedAgencies.includes(agency)}
                            className="mr-2"
                          />
                          {agency}
                        </div>
                      ))}
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>

            {/* Upline Filter */}
            <div>
              <label className="text-xs text-neutral-600 mb-1 block">Filter by Upline</label>
              <Popover open={uplineOpen} onOpenChange={setUplineOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={uplineOpen}
                    className="w-full justify-between"
                  >
                    {selectedUplines.length === 0
                      ? "All Uplines"
                      : `${selectedUplines.length} selected`}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0">
                  <div className="flex flex-col">
                    <div className="flex items-center border-b px-3">
                      <svg className="mr-2 h-4 w-4 shrink-0 opacity-50" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <input
                        className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-neutral-500"
                        placeholder="Search uplines..."
                      />
                    </div>
                    <div className="max-h-64 overflow-auto">
                      {uniqueUplines.map((upline) => (
                        <div className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-2 text-sm outline-none hover:bg-neutral-100 active:bg-neutral-200"
                          key={upline}
                          onClick={() => {
                            setSelectedUplines(prev =>
                              prev.includes(upline)
                                ? prev.filter(u => u !== upline)
                                : [...prev, upline]
                            );
                          }}
                        >
                          <Checkbox
                            checked={selectedUplines.includes(upline)}
                            className="mr-2"
                          />
                          {upline}
                        </div>
                      ))}
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>

            {/* Status Filter */}
            <div>
              <label className="text-xs text-neutral-600 mb-1 block">Filter by Status</label>
              <Popover open={statusOpen} onOpenChange={setStatusOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={statusOpen}
                    className="w-full justify-between"
                  >
                    {selectedStatuses.length === 0
                      ? "All Statuses"
                      : `${selectedStatuses.length} selected`}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0">
                  <div className="flex flex-col">
                    <div className="flex items-center border-b px-3">
                      <svg className="mr-2 h-4 w-4 shrink-0 opacity-50" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <input
                        className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-neutral-500"
                        placeholder="Search statuses..."
                      />
                    </div>
                    <div className="max-h-64 overflow-auto">
                      {uniqueStatuses.map((status) => (
                        <div className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-2 text-sm outline-none hover:bg-neutral-100 active:bg-neutral-200"
                          key={status}
                          onClick={() => {
                            setSelectedStatuses(prev =>
                              prev.includes(status)
                                ? prev.filter(s => s !== status)
                                : [...prev, status]
                            );
                          }}
                        >
                          <Checkbox
                            checked={selectedStatuses.includes(status)}
                            className="mr-2"
                          />
                          <span className="capitalize">{status}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Reset Filters */}
          {(selectedAgencies.length > 0 || selectedUplines.length > 0 || selectedStatuses.length > 0 || searchQuery) && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setSelectedAgencies([]);
                setSelectedUplines([]);
                setSelectedStatuses([]);
                setSearchQuery('');
              }}
            >
              Reset Filters
            </Button>
          )}

          <div className="flex items-center justify-between text-sm text-neutral-600 mt-4">
            <div className="flex items-center gap-3">
              <span>
                Showing {filteredAgents.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1} - {Math.min(currentPage * itemsPerPage, filteredAgents.length)} of {filteredAgents.length} agents
              </span>
              <div className="flex items-center gap-2">
                <span className="text-xs text-neutral-500">Rows per page:</span>
                <Select 
                  value={itemsPerPage.toString()} 
                  onValueChange={(value) => {
                    setItemsPerPage(Number(value));
                    setCurrentPage(1); // Reset to first page when changing page size
                  }}
                >
                  <SelectTrigger className="w-20 h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {totalPages > 1 && <span>Page {currentPage} of {totalPages}</span>}
          </div>

          {/* Bulk Actions */}
          {selectedAgents.length > 0 && (
            <div className="mb-4 flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
              <span className="text-sm font-medium text-blue-900">
                {selectedAgents.length} selected
              </span>
              <Select value={bulkAction} onValueChange={setBulkAction}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Bulk Actions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="change-agency">Change Agency</SelectItem>
                  <SelectItem value="change-upline">Change Upline</SelectItem>
                  <SelectItem value="update-status">Update Status</SelectItem>
                  <SelectItem value="delete">Delete</SelectItem>
                </SelectContent>
              </Select>
              <Button 
                size="sm"
                onClick={() => setShowBulkModal(true)}
                disabled={!bulkAction}
                className="bg-[#d4af37] hover:bg-[#b8941e] text-white"
              >
                Apply
              </Button>
              <Button 
                size="sm"
                variant="outline"
                onClick={() => setSelectedAgents([])}
              >
                Clear
              </Button>
            </div>
          )}

          {/* Table - Scrollable */}
          <div className="overflow-x-auto overflow-y-auto mt-4 rounded-xl border border-neutral-200" style={{ maxHeight: '600px' }}>
            <table className="w-full">
              <thead className="bg-neutral-50 sticky top-0 z-10 border-b border-neutral-200">
                <tr>
                  <th className="text-left text-xs font-semibold text-neutral-600 px-3 py-2.5 w-12">
                    <Checkbox
                      checked={selectedAgents.length === paginatedAgents.length && paginatedAgents.length > 0}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedAgents(paginatedAgents.map(a => a.id));
                        } else {
                          setSelectedAgents([]);
                        }
                      }}
                    />
                  </th>
                  <th className="text-left text-xs font-semibold text-neutral-600 px-3 py-2.5">Name</th>
                  <th className="text-left text-xs font-semibold text-neutral-600 px-3 py-2.5">NPN</th>
                  <th className="text-left text-xs font-semibold text-neutral-600 px-3 py-2.5">Email</th>
                  <th className="text-left text-xs font-semibold text-neutral-600 px-3 py-2.5">Agency</th>
                  <th className="text-left text-xs font-semibold text-neutral-600 px-3 py-2.5">Upline</th>
                  <th className="text-center text-xs font-semibold text-neutral-600 px-3 py-2.5 w-24">Status</th>
                  <th className="text-center text-xs font-semibold text-neutral-600 px-3 py-2.5 w-20">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100 bg-white">
                {paginatedAgents.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center py-12 text-neutral-400">
                      <FileSignature className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p>No agents found</p>
                    </td>
                  </tr>
                ) : (
                  paginatedAgents.map((agent, index) => (
                    <tr key={agent.id || index} className="hover:bg-neutral-50/50 transition-colors group">
                      <td className="px-3 py-2.5">
                        <Checkbox
                          checked={selectedAgents.includes(agent.id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedAgents(prev => [...prev, agent.id]);
                            } else {
                              setSelectedAgents(prev => prev.filter(id => id !== agent.id));
                            }
                          }}
                        />
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex flex-col gap-0.5">
                          <span className="text-sm font-medium text-neutral-800">
                            {agent.first_name} {agent.last_name}
                          </span>
                          {agent.addedBy_name && (
                            <span className="text-xs text-neutral-400">
                              by {agent.addedBy_name}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2.5">
                        <span className="text-xs font-mono text-neutral-600">{agent.npn}</span>
                      </td>
                      <td className="px-3 py-2.5">
                        <span className="text-xs text-neutral-600 truncate block max-w-[180px]" title={agent.email}>
                          {agent.email || <span className="text-neutral-400">—</span>}
                        </span>
                      </td>
                      <td className="px-3 py-2.5">
                        <span className="text-xs text-neutral-600">{agent.agencyName || <span className="text-neutral-400">—</span>}</span>
                      </td>
                      <td className="px-3 py-2.5">
                        <span className="text-xs text-neutral-600">{agent.agentUpline_name || <span className="text-neutral-400">—</span>}</span>
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        <Badge 
                          variant="outline"
                          className={`text-[10px] px-2 py-0.5 font-medium border ${
                            agent.status === 'active' 
                              ? 'bg-green-50 text-green-700 border-green-200' 
                              : agent.status === 'disconnected'
                              ? 'bg-red-50 text-red-700 border-red-200'
                              : 'bg-neutral-50 text-neutral-600 border-neutral-200'
                          }`}
                        >
                          {agent.status || 'N/A'}
                        </Badge>
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center justify-center gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleOpenEditModal(agent)}
                            className="h-7 w-7 p-0 hover:bg-[#d4af37]/10 hover:text-[#d4af37] rounded transition-all opacity-0 group-hover:opacity-100"
                          >
                            <Edit className="w-3.5 h-3.5" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleOpenDeleteModal(agent)}
                            className="h-7 w-7 p-0 hover:bg-red-50 hover:text-red-600 rounded transition-all opacity-0 group-hover:opacity-100"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-neutral-200">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              
              <div className="flex items-center gap-2">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <Button
                      key={pageNum}
                      variant={currentPage === pageNum ? "default" : "outline"}
                      size="sm"
                      onClick={() => setCurrentPage(pageNum)}
                      className={currentPage === pageNum ? "bg-[#d4af37] hover:bg-[#b8941e] text-white rounded-lg" : "rounded-lg"}
                    >
                      {pageNum}
                    </Button>
                  );
                })}
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </div>
          )}
            </CardContent>
          </Card>
        </div>

        {/* Summary Tables - 35% */}
        <div style={{ width: '35%' }} className="space-y-6">
          {/* By Agency Summary */}
          <Card className="border-0 shadow-sm rounded-xl">
            <CardContent className="p-4">
              {/* Header */}
              <div className="mb-3">
                <p className="text-xs text-neutral-500 uppercase tracking-wide">Summary</p>
                <h3 className="text-base font-bold text-neutral-800">By Agency</h3>
              </div>

              {/* Filter and Button */}
              <div className="flex gap-2 mb-3">
                <Select 
                  value={selectedManagingAgency || "all"} 
                  onValueChange={(value) => setSelectedManagingAgency(value === "all" ? "" : value)}
                >
                  <SelectTrigger className="flex-1 h-8 text-xs rounded-lg">
                    <SelectValue placeholder="All Managing Agencies" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Managing Agencies</SelectItem>
                    {uniqueManagingAgencies.map((managing) => (
                      <SelectItem key={managing} value={managing}>
                        {managing}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button 
                  size="sm"
                  onClick={handleOpenAddAgencyModal}
                  className="bg-[#d4af37] hover:bg-[#b8941e] text-white text-xs h-8 rounded-lg whitespace-nowrap"
                >
                  <Plus className="w-3 h-3 mr-1" />
                  Add Agency
                </Button>
              </div>

              {/* Scrollable Table */}
              <div className="overflow-x-auto overflow-y-auto rounded-lg border border-neutral-100" style={{ maxHeight: '400px' }}>
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-neutral-50 to-neutral-100 sticky top-0 z-10">
                    <tr>
                      <th className="text-left text-xs font-semibold text-neutral-700 px-3 py-2.5 first:rounded-tl-lg">Agency</th>
                      <th className="text-left text-xs font-semibold text-neutral-700 px-3 py-2.5">Manager</th>
                      <th className="text-left text-xs font-semibold text-neutral-700 px-3 py-2.5">Managing Agency</th>
                      <th className="text-right text-xs font-semibold text-neutral-700 px-3 py-2.5 last:rounded-tr-lg">Agents</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-100 bg-white">
                    {paginatedAgencies.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="text-center py-8 text-xs text-neutral-400">
                          No data
                        </td>
                      </tr>
                    ) : (
                      paginatedAgencies.map((agency, index) => (
                        <tr key={index} className="hover:bg-neutral-50 transition-all duration-200">
                          <td className={`px-3 py-3 text-xs font-semibold ${!agency.isKnown ? 'text-red-600' : 'text-neutral-800'}`}>
                            {agency.name}
                          </td>
                          <td className={`px-3 py-3 text-xs ${!agency.isKnown ? 'text-red-600' : 'text-neutral-600'}`}>
                            {agency.manager}
                          </td>
                          <td className={`px-3 py-3 text-xs ${!agency.isKnown ? 'text-red-600' : 'text-neutral-600'}`}>
                            {agency.managingAgency}
                          </td>
                          <td className="px-3 py-3 text-xs text-neutral-800 text-right font-bold">{agency.count}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination */}
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-neutral-200">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setAgencyPage(prev => Math.max(1, prev - 1))}
                  disabled={agencyPage === 1}
                  className="text-xs h-7 rounded-lg"
                >
                  Prev
                </Button>
                <span className="text-xs text-neutral-600 font-medium">
                  Page {agencyPage} of {agencyTotalPages || 1}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setAgencyPage(prev => Math.min(agencyTotalPages, prev + 1))}
                  disabled={agencyPage === agencyTotalPages}
                  className="text-xs h-7 rounded-lg"
                >
                  Next
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* By Upline Summary */}
          <Card className="border-0 shadow-sm rounded-xl">
            <CardContent className="p-4">
              {/* Header */}
              <div className="mb-3">
                <p className="text-xs text-neutral-500 uppercase tracking-wide">Summary</p>
                <h3 className="text-base font-bold text-neutral-800">By Upline</h3>
              </div>

              {/* Scrollable Table */}
              <div className="overflow-x-auto overflow-y-auto rounded-lg border border-neutral-100" style={{ maxHeight: '400px' }}>
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-neutral-50 to-neutral-100 sticky top-0 z-10">
                    <tr>
                      <th className="text-left text-xs font-semibold text-neutral-700 px-3 py-2.5 first:rounded-tl-lg">Upline</th>
                      <th className="text-left text-xs font-semibold text-neutral-700 px-3 py-2.5">Agency</th>
                      <th className="text-right text-xs font-semibold text-neutral-700 px-3 py-2.5 last:rounded-tr-lg">Agents</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-100 bg-white">
                    {paginatedUplines.length === 0 ? (
                      <tr>
                        <td colSpan="3" className="text-center py-8 text-xs text-neutral-400">
                          No data
                        </td>
                      </tr>
                    ) : (
                      paginatedUplines.map((upline, index) => (
                        <tr key={index} className="hover:bg-neutral-50 transition-all duration-200">
                          <td className="px-3 py-3 text-xs font-semibold text-neutral-800">{upline.name}</td>
                          <td className="px-3 py-3 text-xs text-neutral-600">{upline.agency}</td>
                          <td className="px-3 py-3 text-xs text-neutral-800 text-right font-bold">{upline.count}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination */}
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-neutral-200">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setUplinePage(prev => Math.max(1, prev - 1))}
                  disabled={uplinePage === 1}
                  className="text-xs h-7 rounded-lg"
                >
                  Prev
                </Button>
                <span className="text-xs text-neutral-600 font-medium">
                  Page {uplinePage} of {uplineTotalPages || 1}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setUplinePage(prev => Math.min(uplineTotalPages, prev + 1))}
                  disabled={uplinePage === uplineTotalPages}
                  className="text-xs h-7 rounded-lg"
                >
                  Next
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Add Agent Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Agent</DialogTitle>
            <DialogDescription>
              Register a new agent in the contracting system
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* First Name */}
            <div>
              <Label htmlFor="firstName">First Name *</Label>
              <Input
                id="firstName"
                value={formData.firstName}
                onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                placeholder="Enter first name"
              />
            </div>

            {/* Last Name */}
            <div>
              <Label htmlFor="lastName">Last Name *</Label>
              <Input
                id="lastName"
                value={formData.lastName}
                onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                placeholder="Enter last name"
              />
            </div>

            {/* NPN with Verification */}
            <div>
              <Label htmlFor="npn">NPN *</Label>
              <div className="flex gap-2">
                <Input
                  id="npn"
                  value={formData.npn}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, npn: e.target.value }));
                    setNpnStatus({ verified: false, checking: false, error: '' });
                  }}
                  placeholder="Enter NPN"
                  className={npnStatus.verified ? 'border-green-500' : npnStatus.error ? 'border-red-500' : ''}
                />
                <Button
                  onClick={() => verifyNPN(formData.npn)}
                  disabled={npnStatus.checking || !formData.npn}
                  size="sm"
                >
                  {npnStatus.checking ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : npnStatus.verified ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    'Verify'
                  )}
                </Button>
              </div>
              {npnStatus.error && <p className="text-xs text-red-600 mt-1">{npnStatus.error}</p>}
              {npnStatus.verified && <p className="text-xs text-green-600 mt-1">✓ NPN available</p>}
            </div>

            {/* FFL Agency */}
            <div>
              <Label htmlFor="agency">FFL Agency *</Label>
              <Select value={formData.agencyId} onValueChange={(value) => setFormData(prev => ({ ...prev, agencyId: value }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select agency" />
                </SelectTrigger>
                <SelectContent>
                  {agencies.map((agency) => (
                    <SelectItem key={agency.id} value={agency.id}>
                      {agency.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Upline NPN with Verification */}
            <div>
              <Label htmlFor="uplineNpn">Upline NPN *</Label>
              <div className="flex gap-2">
                <Input
                  id="uplineNpn"
                  value={formData.uplineNpn}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, uplineNpn: e.target.value }));
                    setUplineStatus({ verified: false, checking: false, name: '', error: '' });
                  }}
                  placeholder="Enter upline NPN"
                  className={uplineStatus.verified ? 'border-green-500' : uplineStatus.error ? 'border-red-500' : ''}
                />
                <Button
                  onClick={() => verifyUplineNPN(formData.uplineNpn)}
                  disabled={uplineStatus.checking || !formData.uplineNpn}
                  size="sm"
                >
                  {uplineStatus.checking ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : uplineStatus.verified ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    'Verify'
                  )}
                </Button>
              </div>
              {uplineStatus.error && <p className="text-xs text-red-600 mt-1">{uplineStatus.error}</p>}
              {uplineStatus.verified && uplineStatus.name && (
                <p className="text-xs text-green-600 mt-1">✓ {uplineStatus.name}</p>
              )}
            </div>

            {/* Status */}
            <div>
              <Label htmlFor="status">Status *</Label>
              <Select value={formData.status} onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="disconnected">Disconnected</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmitForm}
              className="bg-[#d4af37] hover:bg-[#b8941e] text-white"
            >
              Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Agent Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Agent</DialogTitle>
            <DialogDescription>
              Update agent information
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* First Name */}
            <div>
              <Label htmlFor="editFirstName">First Name *</Label>
              <Input
                id="editFirstName"
                value={formData.firstName}
                onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                placeholder="Enter first name"
              />
            </div>

            {/* Last Name */}
            <div>
              <Label htmlFor="editLastName">Last Name *</Label>
              <Input
                id="editLastName"
                value={formData.lastName}
                onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                placeholder="Enter last name"
              />
            </div>

            {/* Email */}
            <div>
              <Label htmlFor="editEmail">Email</Label>
              <Input
                id="editEmail"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                placeholder="Enter email address"
              />
            </div>

            {/* NPN with Change/Verification */}
            <div>
              <Label htmlFor="editNpn">NPN *</Label>
              {!isEditingNPN ? (
                <div className="flex gap-2">
                  <Input
                    id="editNpn"
                    value={formData.npn}
                    disabled
                    className="bg-neutral-50"
                  />
                  <Button
                    onClick={() => setIsEditingNPN(true)}
                    variant="outline"
                    size="sm"
                    className="whitespace-nowrap"
                  >
                    Change
                  </Button>
                </div>
              ) : (
                <>
                  <div className="flex gap-2">
                    <Input
                      id="editNpn"
                      value={formData.npn}
                      onChange={(e) => {
                        setFormData(prev => ({ ...prev, npn: e.target.value }));
                        setNpnStatus({ verified: false, checking: false, error: '' });
                      }}
                      placeholder="Enter NPN"
                      className={npnStatus.verified ? 'border-green-500' : npnStatus.error ? 'border-red-500' : ''}
                    />
                    <Button
                      onClick={() => verifyNPN(formData.npn)}
                      disabled={npnStatus.checking || !formData.npn}
                      size="sm"
                    >
                      {npnStatus.checking ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : npnStatus.verified ? (
                        <Check className="w-4 h-4" />
                      ) : (
                        'Verify'
                      )}
                    </Button>
                    <Button
                      onClick={() => {
                        setFormData(prev => ({ ...prev, npn: originalNPN }));
                        setNpnStatus({ verified: true, checking: false, error: '' });
                        setIsEditingNPN(false);
                      }}
                      variant="ghost"
                      size="sm"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  {npnStatus.error && <p className="text-xs text-red-600 mt-1">{npnStatus.error}</p>}
                  {npnStatus.verified && <p className="text-xs text-green-600 mt-1">✓ NPN available</p>}
                </>
              )}
            </div>

            {/* FFL Agency */}
            <div>
              <Label htmlFor="editAgency">FFL Agency *</Label>
              <Select value={formData.agencyId} onValueChange={(value) => setFormData(prev => ({ ...prev, agencyId: value }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select agency" />
                </SelectTrigger>
                <SelectContent>
                  {agencies.map((agency) => (
                    <SelectItem key={agency.id} value={agency.id}>
                      {agency.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Upline NPN with Verification */}
            <div>
              <Label htmlFor="editUplineNpn">Upline NPN *</Label>
              <div className="flex gap-2">
                <Input
                  id="editUplineNpn"
                  value={formData.uplineNpn}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, uplineNpn: e.target.value }));
                    setUplineStatus({ verified: false, checking: false, name: '', error: '' });
                  }}
                  placeholder="Enter upline NPN"
                  className={uplineStatus.verified ? 'border-green-500' : uplineStatus.error ? 'border-red-500' : ''}
                />
                <Button
                  onClick={() => verifyUplineNPN(formData.uplineNpn)}
                  disabled={uplineStatus.checking || !formData.uplineNpn}
                  size="sm"
                >
                  {uplineStatus.checking ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : uplineStatus.verified ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    'Verify'
                  )}
                </Button>
              </div>
              {uplineStatus.error && <p className="text-xs text-red-600 mt-1">{uplineStatus.error}</p>}
              {uplineStatus.verified && uplineStatus.name && (
                <p className="text-xs text-green-600 mt-1">✓ {uplineStatus.name}</p>
              )}
            </div>

            {/* Status */}
            <div>
              <Label htmlFor="editStatus">Status *</Label>
              <Select value={formData.status} onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="disconnected">Disconnected</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmitEdit}
              className="bg-[#d4af37] hover:bg-[#b8941e] text-white"
            >
              Update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation Modal */}
      <Dialog open={showConfirmModal} onOpenChange={setShowConfirmModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingAgent ? 'Confirm Agent Update' : 'Confirm Agent Registration'}</DialogTitle>
            <DialogDescription>
              Please review the information before confirming
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-4">
            <div className="flex justify-between">
              <span className="text-sm text-neutral-600">Name:</span>
              <span className="text-sm font-medium">{formData.firstName} {formData.lastName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-neutral-600">NPN:</span>
              <span className="text-sm font-medium font-mono">{formData.npn}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-neutral-600">Agency:</span>
              <span className="text-sm font-medium">
                {agencies.find(a => a.id === formData.agencyId)?.name || 'N/A'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-neutral-600">Upline:</span>
              <span className="text-sm font-medium">
                {uplineStatus.name || 'None'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-neutral-600">Status:</span>
              <span className="text-sm font-medium capitalize">
                {formData.status}
              </span>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowConfirmModal(false);
              if (editingAgent) {
                setShowEditModal(true);
              } else {
                setShowAddModal(true);
              }
            }}>
              Back
            </Button>
            <Button 
              onClick={editingAgent ? handleConfirmEdit : handleConfirmAdd}
              className="bg-[#d4af37] hover:bg-[#b8941e] text-white"
            >
              {editingAgent ? 'Confirm & Update' : 'Confirm & Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Deletion</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this agent? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          {deletingAgent && (
            <div className="py-4 bg-red-50 rounded-lg p-4">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-neutral-600">Name:</span>
                  <span className="text-sm font-medium">{deletingAgent.first_name} {deletingAgent.last_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-neutral-600">NPN:</span>
                  <span className="text-sm font-medium font-mono">{deletingAgent.npn}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-neutral-600">Agency:</span>
                  <span className="text-sm font-medium">{deletingAgent.agencyName || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-neutral-600">Status:</span>
                  <span className="text-sm font-medium capitalize">{deletingAgent.status || 'N/A'}</span>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowDeleteModal(false);
              setDeletingAgent(null);
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete Agent
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Action Modal */}
      <Dialog open={showBulkModal} onOpenChange={setShowBulkModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {bulkAction === 'change-agency' && 'Change Agency'}
              {bulkAction === 'change-upline' && 'Change Upline'}
              {bulkAction === 'update-status' && 'Update Status'}
              {bulkAction === 'delete' && 'Confirm Bulk Delete'}
            </DialogTitle>
            <DialogDescription>
              {bulkAction === 'change-agency' && `Update agency for ${selectedAgents.length} selected agent(s)`}
              {bulkAction === 'change-upline' && `Update upline for ${selectedAgents.length} selected agent(s)`}
              {bulkAction === 'update-status' && `Update status for ${selectedAgents.length} selected agent(s)`}
              {bulkAction === 'delete' && `Are you sure you want to delete ${selectedAgents.length} agent(s)? This action cannot be undone.`}
            </DialogDescription>
          </DialogHeader>

          {bulkAction === 'change-agency' && (
            <div className="py-4">
              <Label htmlFor="bulkAgency">Select Agency</Label>
              <Select onValueChange={(value) => handleBulkChangeAgency(value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select agency" />
                </SelectTrigger>
                <SelectContent>
                  {agencies.map((agency) => (
                    <SelectItem key={agency.id} value={agency.id}>
                      {agency.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {bulkAction === 'change-upline' && (
            <div className="py-4">
              <Label htmlFor="bulkUpline">Select Upline Agent</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    className="w-full justify-between"
                  >
                    Select upline agent
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0">
                  <div className="flex flex-col">
                    <div className="flex items-center border-b px-3">
                      <svg className="mr-2 h-4 w-4 shrink-0 opacity-50" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <input
                        className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-neutral-500"
                        placeholder="Search agents..."
                      />
                    </div>
                    <div className="max-h-64 overflow-auto">
                      {agents.map((agent) => (
                        <div className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-2 text-sm outline-none hover:bg-neutral-100 active:bg-neutral-200" onClick={() => handleBulkChangeUpline(agent.id)}>
                          {agent.first_name} {agent.last_name} ({agent.npn})
                        </div>
                      ))}
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          )}

          {bulkAction === 'update-status' && (
            <>
              <div className="py-4">
                <Label htmlFor="bulkStatus">Select Status</Label>
                <Select value={selectedBulkStatus} onValueChange={setSelectedBulkStatus}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="disconnected">Disconnected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => {
                  setShowBulkModal(false);
                  setSelectedBulkStatus('');
                }}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleBulkUpdateStatus}
                  disabled={!selectedBulkStatus}
                  className="bg-[#d4af37] hover:bg-[#b8941e] text-white"
                >
                  Update Status
                </Button>
              </DialogFooter>
            </>
          )}

          {bulkAction === 'delete' && (
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowBulkModal(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleBulkDelete}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                Delete {selectedAgents.length} Agent(s)
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Agency Modal */}
      <Dialog open={showAddAgencyModal} onOpenChange={setShowAddAgencyModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Agency</DialogTitle>
            <DialogDescription>
              Create a new agency record
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Agency Name */}
            <div>
              <Label htmlFor="agencyName">Agency Name *</Label>
              <Input
                id="agencyName"
                value={agencyFormData.name}
                onChange={(e) => setAgencyFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter agency name"
              />
            </div>

            {/* Manager - Agent Dropdown */}
            <div>
              <Label htmlFor="agencyManager">Manager</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    className="w-full justify-between"
                  >
                    {agencyFormData.agentmanager_id 
                      ? agents.find(a => a.id === agencyFormData.agentmanager_id)?.first_name + ' ' + agents.find(a => a.id === agencyFormData.agentmanager_id)?.last_name
                      : 'Select manager'}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0">
                  <div className="flex flex-col">
                    <div className="flex items-center border-b px-3">
                      <svg className="mr-2 h-4 w-4 shrink-0 opacity-50" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <input
                        className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-neutral-500"
                        placeholder="Search agents..."
                      />
                    </div>
                    <div className="max-h-64 overflow-auto">
                      {agents.map((agent) => (
                        <div className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-2 text-sm outline-none hover:bg-neutral-100 active:bg-neutral-200"
                          key={agent.id}
                          onClick={() => setAgencyFormData(prev => ({ ...prev, agentmanager_id: agent.id }))}
                        >
                          {agent.first_name} {agent.last_name} ({agent.npn})
                        </div>
                      ))}
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>

            {/* Managing Agency - Agency Dropdown */}
            <div>
              <Label htmlFor="managingAgency">Managing Agency</Label>
              <Select 
                value={agencyFormData.managingagency_id} 
                onValueChange={(value) => setAgencyFormData(prev => ({ ...prev, managingagency_id: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select managing agency" />
                </SelectTrigger>
                <SelectContent>
                  {agencies.map((agency) => (
                    <SelectItem key={agency.id} value={agency.id}>
                      {agency.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddAgencyModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmitAddAgency}
              className="bg-[#d4af37] hover:bg-[#b8941e] text-white"
            >
              Add Agency
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
